import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';
import supabase from '../services/supabaseClient';
import { getDefaultPermissions } from '../models/permissions';
import { logActivity } from '../services/auditService';
import { ACTIVITY_TYPES } from '../models/activityTypes';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Load user from localStorage on app start
  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('forage_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setAuthenticated(true);
        } catch (error) {
          console.error('Erreur lors du chargement de l\'utilisateur:', error);
          localStorage.removeItem('forage_user');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // Fetch user from database (without status filter to check status separately)
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (error || !users) {
        message.error('Email ou mot de passe incorrect');
        return { success: false };
      }

      // Check if account is active
      if (users.status !== 'active') {
        message.error('Compte vérrouillé. Contactez un administrateur.');
        return { success: false };
      }

      // Simple password validation (in production, use bcrypt on backend)
      // For now, we'll do basic string comparison
      const isPasswordValid = password === users.password;

      if (isPasswordValid) {
        // Update last login
        await supabase
          .from('users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', users.id);

        const userData = {
          id: users.id,
          email: users.email,
          firstName: users.first_name,
          lastName: users.last_name,
          role: users.role,
          permissions: users.permissions || getDefaultPermissions(users.role)
        };

        setUser(userData);
        setAuthenticated(true);
        localStorage.setItem('forage_user', JSON.stringify(userData));
        message.success(`Bienvenue ${users.first_name}!`);
        
        // Enregistrer l'activité de connexion
        try {
          await logActivity(
            ACTIVITY_TYPES.USER_LOGIN,
            users.id,
            'user',
            users.id,
            { email: users.email },
            `Connexion - ${users.email}`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la connexion:', auditError);
        }
        
        return { success: true, user: userData };
      } else {
        message.error('Email ou mot de passe incorrect');
        return { success: false };
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      message.error('Erreur lors de la connexion');
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const currentUser = user;
      
      // Enregistrer l'activité de déconnexion avant de supprimer l'utilisateur
      if (currentUser) {
        try {
          await logActivity(
            ACTIVITY_TYPES.USER_LOGOUT,
            currentUser.id,
            'user',
            currentUser.id,
            { email: currentUser.email },
            `Déconnexion - ${currentUser.email}`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la déconnexion:', auditError);
        }
      }
      
      setUser(null);
      setAuthenticated(false);
      localStorage.removeItem('forage_user');
      message.success('Vous avez été déconnecté');
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      message.error('Erreur lors de la déconnexion');
      return { success: false, error };
    }
  };

  // Register function (for admin use)
  const register = async (email, firstName, lastName, password, role = 'operator') => {
    try {
      setLoading(true);

      // Check if email already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        message.error('Cet email est déjà utilisé');
        return { success: false };
      }

      // Create new user
      const { data: newUser, error } = await supabase
        .from('users')
        .insert([
          {
            id: `user-${Date.now()}`,
            email,
            first_name: firstName,
            last_name: lastName,
            password, // In production, hash this password
            role,
            status: 'active'
          }
        ])
        .select()
        .single();

      if (error) {
        message.error(`Erreur lors de la création de l'utilisateur: ${error.message}`);
        return { success: false, error };
      }

      message.success('Utilisateur créé avec succès');
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      message.error('Erreur lors de l\'enregistrement');
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    authenticated,
    login,
    logout,
    register
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};

export default AuthContext;
